import useAPI from "@toothfairy/shared-api/useApi";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import React, { Suspense, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import envConfig from "../../../envConfig";
import Files from "../../API/Files";
import TrainingLogHelper from "../../API/TrainingLog/trainingLogHelper";
import Constants from "../../Constants";
import useWorkspaces from "../../Hooks/useWorkspaces";
import TrainingScreen from "./TrainingScreen";
import trainingLogHelper from "../../API/TrainingLog/trainingLogHelper";
dayjs.extend(isBetween);

const TrainingScreenContainer = ({ navigation }) => {
  const {
    getActiveWorkspace,
    isPriorityTrainingEnabled,
    isTrainingEnabled,
    getTrainingLogInfo,
  } = useWorkspaces();

  const trainingTypes = Constants.TRAINING_TYPES;

  const { t } = useTranslation();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const [selectedTopics, setSelectedTopics] = useState([]);
  const { user } = AppUser.useUser();
  const AppToasty = AppToast.useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTrainingInitiationModalOpen, setIsTrainingInitiationModalOpen] =
    useState(false);
  const [isTrainingFineTuningModalOpen, setIsTrainingFineTuningModalOpen] =
    useState(false);
  const [isModalOptionsOpen, setIsModalOptionsOpen] = useState(false);
  const [manageModelItem, setManageModelItem] = useState(null);
  const [isFineTunedModelModalOpen, setIsFineTunedModelModalOpen] =
    useState(false);
  const [selectedType, setSelectedType] = useState(trainingTypes[0]?.key);
  const [typeFilter, setTypeFilter] = useState("all");
  const {
    data: S3downloadUrlData,
    loading: urldownloadGenerationInProgress,
    apiRequest: S3downloadUrlRequest,
    response: S3downloadResponse,
  } = useAPI(Files.downloadUrlGeneration, envConfig);
  const [start, setStart] = useState(
    dayjs().subtract(3, "months").startOf("month").toDate()
  );
  const [end, setEnd] = useState(dayjs().endOf("month").toDate());
  const [users, setUsers] = useState(null);
  const [trainingConfig, setTrainingConfig] = useState({
    datasetOnly: !isTrainingEnabled,
    baseModel: "none",
  });
  const [statusFilter, setStatusFilter] = useState("noFilter");
  const [displayedLogs, setDisplayedLogs] = useState([]);
  const searchTimeoutRef = useRef(null);
  const handleFilterChange = (v) => {
    setStatusFilter(v);
  };
  const logStatusColors = {
    dispatched: "darkGrey",
    swapModelRequested: "darkGrey",
    inProgress: "orangeBtnColor",
    completed: "green",
    inError: "red",
    cancellationRequested: "orangeBtnColor",
    cancelled: "red",
    swapping: "orangeBtnColor",
    swapModelFailed: "red",
  };
  const handleUpdateStartDate = (v) => {
    setStart(v);
  };
  const handleUpdateEndDate = (v) => {
    setEnd(v);
  };
  const loadTrainingLogs = async (workspaceID) => {
    let resultLogs = await trainingLogHelper.getTrainingLogsByWorkspaceID(
      workspaceID
    );
    resultLogs = resultLogs?.data?.trainingLogsByWorkspace?.items?.filter(
      (u) => u?._deleted != true
    );
    dispatch("SET_TRAINING_LOGS", resultLogs);
  };
  const handleFineTuningSetupOpening = (trainingLogId) => {
    setTrainingConfig({
      ...getActiveWorkspace()?.trainingConfig,
      learningRateRC: Constants.learningRateRC,
      datasetTestSizeThresholdRC: Constants.datasetTestSizeThresholdRC,
      testSizeRC: Constants.testSizeRC,
      trainingEpochsRC: Constants.baseTrainingRCEpochs,
      batchSizeRC: Constants.batchSizeRC,
      loraRRC: Constants.loraR,
      loraAlphaRC: Constants.loraAlpha,
      loraDropoutRC: Constants.loraDropout,
      evaluationNumberRC: Constants.evaluationNumber,
      loraLayersRC: Constants.loraLayers,
      trainOnInputsRC: Constants.trainOnInputs,
      warmUpLearningRateRC: Constants.warmUpLearningRate,
      peakLearningRateRC: Constants.peakLearningRate,
      checkpointsNumberRC: Constants.checkpointsNumber,
      datasetOnly: false,
      // GRPO defaults
      grpoNumGenerations: 4,
      grpoMaxCompletionLength: 512,
      grpoRewardFuncs: "default",
      // KTO defaults
      ktoBeta: 0.1,
      ktoDesirableWeight: 1.0,
      ktoUndesirableWeight: 1.0,
      // CPT defaults
      learningRateCPT: 0.00005,
      trainingEpochsCPT: 3,
    });
    setIsModalOpen(false);
    setIsTrainingFineTuningModalOpen(true);
  };
  const handleFineTuningStart = async (trainingLogId, trainingConfig) => {
    const trainingLog = getTrainingLogInfo(trainingLogId);
    if (trainingConfig?.loraLayersRC?.length > 0)
      // Items come either as {id} objects (multiselect) or plain strings
      // (advanced JSON overrides) — handle both.
      trainingConfig.loraLayersRC = trainingConfig.loraLayersRC
        .map((u) => u?.id ?? u)
        ?.join(",");
    else {
      trainingConfig.loraLayersRC = Constants.loraLayers;
    }
    const existingConfig =
      typeof trainingLog?.trainingConfig === "string"
        ? JSON.parse(trainingLog?.trainingConfig)
        : trainingLog?.trainingConfig;

    try {
      const trainingLog = getTrainingLogInfo(trainingLogId);
      const resultTrainingLog = await trainingLogHelper.updateTrainingLog(
        trainingLogId,
        [
          {
            property: "status",
            value: "fineTuningRequested",
          },
          {
            property: "trainingConfig",
            value: JSON.stringify({
              ...existingConfig,
              ...trainingConfig,
            }),
          },
        ]
      );
      // fidn index of the training log
      await loadTrainingLogs(getActiveWorkspace()?.id);
      AppToasty.show(t("training.updateStarted"), {
        placement: "top",
        type: "success",
      });
    } catch (error) {
      console.error("Error while creating training log", error);
      AppToasty.show(t("training.updateFailed"), {
        placement: "top",
        type: "danger",
      });
    } finally {
      setIsModalOpen(false);
      setIsTrainingFineTuningModalOpen(false);
    }
  };

  const handleTrainingInitiation = async () => {
    if (trainingConfig?.name && /[^a-zA-Z0-9_]/.test(trainingConfig.name)) {
      console.error(
        "Training config name contains spaces, special characters or hyphens"
      );
      AppToasty.show(t("training.creationFailedName"), {
        placement: "top",
        type: "danger",
      });
      // Handle the error or invalid input here
      return;
    }
    try {
      const trainingConfigSnapshot = JSON.parse(
        JSON.stringify(getActiveWorkspace()?.trainingConfig)
      );

      // RLVR compound key: the training-log type MUST be grpoTraining
      // (backend routing); RL mode is signalled via rl.enabled in the
      // training config, which the dataset worker reads to build the RL
      // task manifest instead of conversations.
      const isRlvrSelection = selectedType === "grpoTraining+rlvr";
      const trainingLogType = isRlvrSelection
        ? "grpoTraining"
        : selectedType || trainingTypes[0]?.key;

      const resultTrainingLog = await trainingLogHelper.createTrainingLog({
        createdBy: user.id,
        status: "dispatched",
        type: trainingLogType,
        workspaceID: state.activeWorkspaceID,
        name: trainingConfig?.name,
        description: trainingConfig?.description,
        trainingConfig: JSON.stringify({
          ...trainingConfigSnapshot,
          datasetOnly: false,
          baseModel: trainingConfig?.baseModel,
          topics: selectedTopics?.map((u) => u.id),
          ...(isRlvrSelection ? { rl: { enabled: true, env: "tfcode" } } : {}),
          enableManagedSpotTraining: !isPriorityTrainingEnabled()
            ? true
            : getActiveWorkspace()?.trainingConfig?.enableManagedSpotTraining,
        }),
      });
      const updatedLogs = [...state?.trainingLogs];
      updatedLogs.unshift(resultTrainingLog);
      dispatch("SET_TRAINING_LOGS", updatedLogs);
      AppToasty.show(t("training.created"), {
        placement: "top",
        type: "success",
      });
    } catch (error) {
      console.error("Error while creating training log", error);
      AppToasty.show(t("training.creationFailed"), {
        placement: "top",
        type: "danger",
      });
    } finally {
      setIsModalOpen(false);
      setIsTrainingInitiationModalOpen(false);
      setIsModalOptionsOpen(false);
    }
  };
  const handleManageModel = (item) => {
    if (!item?.id) return;
    setManageModelItem(item);
    setIsFineTunedModelModalOpen(true);
  };
  const markTrainingLogAsActive = async (id) => {
    try {
      const result = await TrainingLogHelper.updateTrainingLog(id, [
        {
          property: "lastActivation",
          value: Date.now(),
        },
        {
          property: "status",
          value: "swapModelRequested",
        },
      ]);
      const objectIndex = state?.trainingLogs.findIndex((log) => log.id === id);
      if (objectIndex >= 0) {
        state.trainingLogs[objectIndex] = result;
        dispatch("SET_TRAINING_LOGS", state.trainingLogs);
      }
    } catch (error) {
      throw error;
    }
  };
  const handleFieldUpdate = async (id, property, value) => {
    try {
      const result = await TrainingLogHelper.updateTrainingLog(id, [
        {
          property,
          value,
        },
      ]);
      const objectIndex = state?.trainingLogs.findIndex((log) => log.id === id);
      if (objectIndex >= 0) {
        state.trainingLogs[objectIndex] = result;
        dispatch("SET_TRAINING_LOGS", state.trainingLogs);
      }
    } catch (error) {
      throw error;
    }
  };
  const handleTrainingCancellation = async (id) => {
    try {
      const result = await TrainingLogHelper.updateTrainingLog(id, [
        { property: "status", value: "cancellationRequested" },
      ]);
      AppToasty.show(t("training.cancellationRequested"), {
        placement: "top",
        type: "success",
      });
      const objectIndex = state?.trainingLogs.findIndex((log) => log.id === id);
      if (objectIndex >= 0) {
        state.trainingLogs[objectIndex] = result;
        dispatch("SET_TRAINING_LOGS", state.trainingLogs);
      }
    } catch (error) {
      AppToasty.show(t("training.cancellationFailed"), {
        placement: "top",
        type: "danger",
      });
    }
  };
  const handleDeleteTrainingLog = async (id) => {
    try {
      await trainingLogHelper.deleteTrainingLog(id);
      AppToasty.show(t("training.deletionCaption"), {
        placement: "top",
        type: "success",
      });
      const trainingLogs = state?.trainingLogs.filter((u) => u.id !== id);
      dispatch("SET_TRAINING_LOGS", trainingLogs);
    } catch (error) {
      AppToasty.show(t("training.deletionFailed"), {
        placement: "top",
        type: "danger",
      });
    }
  };
  const isTrainingLogIdActive = (id) => {
    return (
      getActiveWorkspace()?.nlpUrl?.includes(id) ||
      getActiveWorkspace()?.qaUrl?.includes(id)
    );
  };
  useEffect(() => {
    let validLogs = state?.trainingLogs?.filter(
      (u) =>
        (typeFilter === "all" || u.type === typeFilter) &&
        (u.status === statusFilter || statusFilter === "noFilter") &&
        dayjs(new Date(u._lastChangedAt)).isBetween(start, end, null, "[]")
    );
    const activeTrainingLogIndex = validLogs.findIndex((log) =>
      isTrainingLogIdActive(log.id)
    );
    if (activeTrainingLogIndex >= 0) {
      let activeTrainingLog = validLogs[activeTrainingLogIndex];
      activeTrainingLog.isActive = true;
      validLogs.splice(activeTrainingLogIndex, 1);
      validLogs.unshift(activeTrainingLog);
    }
    setDisplayedLogs(validLogs);
  }, [
    typeFilter,
    JSON.stringify(state?.trainingLogs),
    statusFilter,
    start,
    end,
  ]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);
  function download_files(files) {
    function download_next(i) {
      if (i >= files.length) {
        return;
      }
      var a = document.createElement("a");
      a.href = files[i].download;
      a.target = "_parent";
      // Use a.download if available, it prevents plugins from opening.
      if ("download" in a) {
        a.download = files[i].filename;
      }
      // Add a to the doc for click to work.
      (document.body || document.documentElement).appendChild(a);
      if (a.click) {
        a.click(); // The click method is supported by most browsers.
      } else {
        $(a).click(); // Backup using jquery
      }
      // Delete the temporary link.
      a.parentNode.removeChild(a);
      // Download the next file with a small timeout. The timeout is necessary
      // for IE, which will otherwise only download the first file.
      setTimeout(function () {
        download_next(i + 1);
      }, 2000);
    }
    // Initiate the first download.
    download_next(0);
  }
  const handleTrainingDataDownload = async ({
    workspaceid,
    logId,
    type,
    selection,
  }) => {
    try {
      AppToasty.show(t("training.downloadStarted"), {
        placement: "bottom",
        type: "success",
      });
      const result = await Promise.all(
        selection?.map((u) =>
          S3downloadUrlRequest({
            workspaceid,
            logId,
            type,
            // Static option ids resolve via DOWNLOAD_OPTIONS; dynamic ids
            // (e.g. checkpoint-50/adapter_model.safetensors) ARE the filename.
            filename: (
              Constants.DOWNLOAD_OPTIONS.find(
                (item) => item.type === type.toUpperCase() && item.id === u
              ) || { filename: u }
            ).filename,
            context: "training",
          })
        )
      );

      download_files(
        result?.map((u, i) => {
          return {
            download: u.url,
            // Sanitise slashes from dynamic checkpoint ids so the saved
            // filename stays a single valid file name.
            filename:
              "TR-" +
              logId +
              "-" +
              String(selection[i]).replace(/[^a-zA-Z0-9._-]/g, "-"),
          };
        })
      );
    } catch (error) {
      console.error("Training data download error", error);
      AppToasty.show(t("training.downloadFailed"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <TrainingScreen
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        isTrainingInitiationModalOpen={isTrainingInitiationModalOpen}
        setIsTrainingInitiationModalOpen={setIsTrainingInitiationModalOpen}
        isTrainingFineTuningModalOpen={isTrainingFineTuningModalOpen}
        setIsTrainingFineTuningModalOpen={setIsTrainingFineTuningModalOpen}
        isModalOptionsOpen={isModalOptionsOpen}
        setIsModalOptionsOpen={setIsModalOptionsOpen}
        setSelectedTopics={setSelectedTopics}
        selectedTopics={selectedTopics}
        handleFieldUpdate={handleFieldUpdate}
        trainingConfig={trainingConfig}
        setTrainingConfig={setTrainingConfig}
        onUpdateStartDate={handleUpdateStartDate}
        onUpdateEndDate={handleUpdateEndDate}
        onFilterChange={handleFilterChange}
        statusFilter={statusFilter}
        startDate={start}
        endDate={end}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        trainingTypes={trainingTypes}
        handleTrainingInitiation={handleTrainingInitiation}
        handleFineTuningStart={handleFineTuningStart}
        handleFineTuningSetupOpening={handleFineTuningSetupOpening}
        handleTrainingCancellation={handleTrainingCancellation}
        handleDeleteTrainingLog={handleDeleteTrainingLog}
        markTrainingLogAsActive={markTrainingLogAsActive}
        trainingLogs={displayedLogs}
        hasAnyData={state?.trainingLogs?.length > 0}
        logStatusColors={logStatusColors}
        trainingInProgress={false}
        isSwapRequested={
          state?.trainingLogs?.filter((u) => u?.status === "swapModelRequested")
            ?.length > 0
        }
        isSwapingInProgress={
          getActiveWorkspace()?.nlpUrlStatus === "swapping" ||
          getActiveWorkspace()?.qaUrlStatus === "swapping"
        }
        users={users}
        handleDataDownload={handleTrainingDataDownload}
        manageModelItem={manageModelItem}
        setManageModelItem={setManageModelItem}
        isFineTunedModelModalOpen={isFineTunedModelModalOpen}
        setIsFineTunedModelModalOpen={setIsFineTunedModelModalOpen}
        workspaceToken={getActiveWorkspace()?.workspaceToken}
        handleManageModel={handleManageModel}
      />
    </Suspense>
  );
};

export default TrainingScreenContainer;
